<script lang="ts" setup>
import InputDecorator from './InputDecorator.vue';
import type {InputDecoratorProps} from './InputDecorator.vue';
import LoadingPlaceholder from "@Components/Loading/LoadingPlaceholder.vue";
import {computed} from "vue";
import {useFieldsMapping} from '@Common/Composables/fields-mapping';
import {HelpPosition} from './InputDecorator.vue';

const {doesHelpRequireLabel} = useFieldsMapping();

const props = withDefaults(defineProps<{
    id?: string;
    name?: string;
    value?: boolean;
    valueLabel?: string;
    loading?: boolean;
    disabled?: boolean;
} & InputDecoratorProps>(), {
    id: '',
    name: '',
    loading: false,
    disabled: false,
    valueLabel: '',
});

const emit = defineEmits(['update', 'blur']);

const label = computed(() => {
    if (props.label) {
        return props.label;
    }
    if (props.valueLabel && doesHelpRequireLabel(props.help || '', props.helpPosition || HelpPosition.None)) {
        return props.valueLabel;
    }
    return '';
});

const isValueLabelRequired = computed(() => {
    if (doesHelpRequireLabel(props.help || '', props.helpPosition || HelpPosition.None)) {
        return false;
    }
    return !(!props.required || props.label);
});

</script>
<template>
    <InputDecorator
        :for-id="id"
        :label="label"
        :help="help"
        :help-position="helpPosition"
        :errors="errors"
        :required="required"
    >
        <div v-if="!loading" class="input-container">
            <input class="input" type="checkbox"
                   :id="id"
                   :name="name"
                   :disabled="disabled"
                   :checked="value"
                   @change="(e) => emit('update', (e.target as HTMLInputElement).checked)"
                   @blur="emit('blur')"
            />
            <label :class="['checkbox-label', {disabled}]" :for="id">
                {{ valueLabel }}
                <span v-if="isValueLabelRequired" class="required-dot">*</span>
            </label>
        </div>
        <LoadingPlaceholder height="51px" width="400px" v-else/>
    </InputDecorator>
</template>
<style lang="scss" scoped>
@import '@Common/Styles/variables';

.form-group {
    .input-container {
        display: block;
        min-height: 27px;
        padding-left: 30px;
        position: relative;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        z-index: 1;

        .input {
            height: 23.5px;
            left: 0;
            opacity: 0;
            position: absolute;
            width: 20px;
            z-index: -1;
            margin: 0;

            &:focus ~ .checkbox-label::before, &:active ~ .checkbox-label::before {
                border-color: var(--gray-500, #{map-get($yapla-gray, 500)});
                box-shadow: 0 0 0 0.2rem var(--blue-200, #{map-get($yapla-orange, 200)});
            }

            &:checked ~ .checkbox-label::before {
                background-color: var(--primary, #{map-get($yapla-orange, 500)});
                border-color: var(--primary, #{map-get($yapla-orange, 500)});
                color: white;
            }

            &:checked ~ .checkbox-label::after {
                background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%23fff' d='m6.564.75-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3E%3C/svg%3E");
            }

            &:disabled:checked ~ .checkbox-label::before {
                background-color: var(--gray-300, #{map-get($yapla-gray, 300)});
                border-color: var(--gray-300, #{map-get($yapla-gray, 300)});
                color: var(--gray-300, #{map-get($yapla-gray, 300)});
            }
        }

        .checkbox-label {
            margin-bottom: 0;
            position: relative;
            vertical-align: top;
            min-width: auto;
            max-width: none;
            font-weight: normal;
            display: inline-block;
            line-height: 27px;

            &::before {
                transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                content: "";
                border-radius: 2px;
                background-color: white;
                border: 1px solid var(--gray-400, #{map-get($yapla-gray, 400)});
                pointer-events: none;
                display: block;
                height: 20px;
                left: -30px;
                position: absolute;
                top: 3.5px;
                width: 20px;
            }

            &::after {
                content: "";
                display: block;
                height: 20px;
                left: -30px;
                position: absolute;
                top: 3.5px;
                width: 20px;
                background: 50%/50% 50% no-repeat;
            }

            .required-dot {
                color: var(--red-500, map-get($yapla-red, 500));
                content: "*";
            }

            &.disabled {
                color: var(--gray-500, #{map-get($yapla-gray, 500)});
            }
        }
    }
}
</style>